// Scoring algorithm copied from the legacy PasswordStrengthIndicator.
import type {StrengthLevel} from "@/primitives/PasswordStrengthMeter";
import {containsAtLeastOneOf} from "@/shared/utils/string";

export interface StrengthResultOption {
	name: StrengthLevel;
	order: number;
	minimumScore: number;
}

export interface StrengthRule {
	name: string;
	characters: string;
}

export const DEFAULT_STRENGTH_RESULT_OPTIONS: StrengthResultOption[] = [
	{
		name: "very-weak",
		order: 1,
		minimumScore: 0,
	},
	{
		name: "weak",
		order: 2,
		minimumScore: 6,
	},
	{
		name: "medium",
		order: 3,
		minimumScore: 12,
	},
	{
		name: "strong",
		order: 4,
		minimumScore: 18,
	},
	{
		name: "very-strong",
		order: 5,
		minimumScore: 24,
	},
];

export const DEFAULT_STRENGTH_RULES: StrengthRule[] = [
	{
		name: "lowercase",
		characters: "abcdefghijklmnopqrstuvwxyzäöüß",
	},
	{
		name: "uppercase",
		characters: "ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÜ",
	},
	{
		name: "number",
		characters: "0123456789",
	},
	{
		name: "symbol",
		characters: "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~¡¢£¤¥¦§¨",
	},
];

// if the character variance is equal to or below this value there will be a 100% penalty on the calculated score
const CHARACTER_VARIANCE_PENALTY_LOW_THRESHOLD = 4;
// if the character variance is equal to or above this value there will be no penalty on the calculated score
const CHARACTER_VARIANCE_PENALTY_HIGH_THRESHOLD = 8;

function calculateScorePercentagePenalty(
	letterVariance: number,
	lowerThreshold: number = CHARACTER_VARIANCE_PENALTY_LOW_THRESHOLD,
	upperThreshold: number = CHARACTER_VARIANCE_PENALTY_HIGH_THRESHOLD,
): number {
	if (letterVariance >= upperThreshold) {
		return 0;
	} else if (letterVariance <= lowerThreshold) {
		return 1;
	} else {
		return (upperThreshold - letterVariance) / (upperThreshold - lowerThreshold);
	}
}

/**
 * Determines the strength of a given password by assigning it a numerical score.
 * The underlying algorithm uses two factors for determining the score: the length
 * of the password and the variance of character traits.
 *
 * The used formula is: password length + (password symbol variance - 1) * 4.
 * Additionally, a percentage penalty for low symbol variance (e.g. only "a"
 * characters) will be applied according to the configuration.
 *
 * The default result options are:
 *   0 -  5 very weak
 *   6 - 11 weak
 *  12 - 17 medium
 *  18 - 23 strong
 *  >24     very strong
 *
 * @param password the password to determine the strength for
 * @param strengthRules the rules to use during the strength determination (optional)
 * @param strengthResultOptions the possible strength result options (optional)
 */
export function determinePasswordStrength(
	password: string,
	strengthRules: StrengthRule[] = DEFAULT_STRENGTH_RULES,
	strengthResultOptions: StrengthResultOption[] = DEFAULT_STRENGTH_RESULT_OPTIONS,
): StrengthResultOption | null {
	if (strengthRules?.length === 0 || strengthResultOptions?.length === 0) {
		throw new Error("No strength rules or strength result options were passed");
	}

	const passwordLength = password.length;
	const passwordSymbolVariance = Array.from(new Set(password.split(""))).length;
	const passwordLetterGroupVariance = strengthRules.filter(rule => containsAtLeastOneOf(password, rule.characters)).length;
	const scorePercentagePenalty = calculateScorePercentagePenalty(passwordSymbolVariance);
	const passwordScore = (passwordLength + ((passwordLetterGroupVariance - 1) * 4)) * (1 - scorePercentagePenalty);

	const orderedFulfilledResultOptions = strengthResultOptions
		.filter(resultOption => passwordScore >= resultOption.minimumScore)
		.sort((a, b) => b.minimumScore - a.minimumScore);

	return orderedFulfilledResultOptions.length > 0 ? orderedFulfilledResultOptions[0] : null;
}
